import { Description } from '@monorepo/shared/componentsV2/Description';
import { TextField } from '@monorepo/shared/componentsV2/fields/TextField';
import type { SelectOption } from '@monorepo/shared/componentsV2/Select/Select';
import {
  StyledFormGroup,
  StyledInput,
  StyledSelect,
} from '@monorepo/shared/componentsV2/styled/form';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import {
  compose,
  hasNoInvalidCharacters,
  hasNoWhitespace,
  isRequired,
  makeIsNoLongerThan,
} from '@monorepo/shared/utils/validators';
import {
  Category,
  getCategoryDisplayString,
  MAX_FORM_TEMPLATE_NAME_LENGTH,
} from 'mapistry-shared';
import React from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { isValidFormTemplateSlug } from '../GenerateFormTemplatePage/isValidFormTemplateSlug';

const CATEGORY_OPTIONS: SelectOption[] = Object.values(Category)
  .map((category) => ({
    value: category,
    label: getCategoryDisplayString(category),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const StyledDescription = styled(Description)`
  margin-bottom: 1.5rem;
`;

function FormTemplateNameField() {
  return (
    <StyledFormGroup row>
      <StyledInput
        label="Form Name"
        name="name"
        required
        validate={compose(
          isRequired,
          makeIsNoLongerThan(MAX_FORM_TEMPLATE_NAME_LENGTH),
        )}
      />
    </StyledFormGroup>
  );
}

function FormTemplateSlugField({ isEditMode }: { isEditMode: boolean }) {
  const isSuperAdmin = useIsSuperAdmin();
  const {
    input: { value: slug },
  } = useField('slug');

  if (!isSuperAdmin) return null;

  if (isEditMode)
    return <StyledDescription>Form Template Slug: {slug}</StyledDescription>;

  return (
    <StyledFormGroup row>
      <TextField
        label="Form template slug"
        name="slug"
        fullWidth
        required
        validate={compose(
          isRequired,
          isValidFormTemplateSlug,
          hasNoWhitespace,
          hasNoInvalidCharacters('/'),
        )}
      />
    </StyledFormGroup>
  );
}

function FormTemplateCategoryField() {
  return (
    <StyledFormGroup row>
      <StyledSelect
        label="Compliance Category"
        name="category"
        options={CATEGORY_OPTIONS}
        showBlankOption
      />
    </StyledFormGroup>
  );
}

type FormTemplateDetailsFieldsProps = {
  isEditMode: boolean;
};

export function FormTemplateDetailsFields({
  isEditMode,
}: FormTemplateDetailsFieldsProps) {
  return (
    <>
      <FormTemplateNameField />
      <FormTemplateSlugField isEditMode={isEditMode} />
      <FormTemplateCategoryField />
    </>
  );
}
